// Wyświetlanie na tym telefonie (USTALENIA 6, SPIS N-2): motyw jasny/ciemny oraz - jak w Androidzie - rozmiar
// wyświetlania (powiększa wszystko: kafle, przyciski, odstępy) i rozmiar czcionki (sam tekst). Lokalnie w telefonie,
// bo to cecha ekranu, nie konta. Pierwszy motyw ustawia public/motyw.js przed malowaniem (ten sam klucz).
//
// Rozmiar wyświetlania na telefonie i tablecie = szerokość układu (viewport) węższa o skalę: strona układa się jak na
// mniejszym ekranie i przeglądarka powiększa ją do pełnej szerokości - punkty przełamania (RWD) działają, nic nie
// wychodzi poza ekran. Na komputerze odpowiednikiem jest powiększenie przeglądarki (Ctrl +/−) - strona go nie ustawia
// (`zoom` z kodu psuł punkty przełamania i położenie okienek przy zmianie rozmiaru okna). Czcionka - wszędzie: bazowy
// rozmiar strony (`html`, baza 85% - styles/wspolne-baza.css; stopień 1 = „100%” w oknie), style piszą wymiary w rem.
//
// „Nie wygaszaj ekranu” (stara APP: Ustawienia, osobno przy każdym urządzeniu - ale ekran jest jeden i blokada trzymała
// się, gdy zaznaczone było DOWOLNE urządzenie; tu jeden przełącznik na telefon). Blokada działa tylko przy aplikacji na
// wierzchu, a system zwalnia ją sam (tło, oszczędzanie energii) - odzyskujemy ją przy powrocie, dotknięciu i strażnikiem.
// Aplikacja ze sklepu: natywny KeepAwake (WebView Androida nie ma `wakeLock`) - system sam go nie zwalnia.
//
// Nagłówek pulpitu (właściciel 24.10): przewija się z ekranem (domyślnie) albo stoi przyklejony u góry
// (`data-iot-naglowek="staly"` na html) i może być zwinięty do jednego niskiego wiersza - oba na tym telefonie.
import { useSyncExternalStore } from "react";
import { KeepAwake } from "@capacitor-community/keep-awake";
import { natywna } from "./instalacja";

export type Motyw = "dark" | "light";
/** Stan blokady wygaszania: trzymana / czeka na aplikację na wierzchu / przeglądarka odmówiła. */
export type BlokadaEkranu = "trzyma" | "czeka" | "odmowa";

const KLUCZ = "twf:motyw";
const KLUCZ_EKRAN = "twf:ekran";
const KLUCZ_CZCIONKA = "twf:czcionka";
const KLUCZ_NIE_WYGASZAJ = "twf:nie-wygaszaj";
const KLUCZ_NAGLOWEK_STALY = "twf:naglowek-staly";
const KLUCZ_NAGLOWEK_ZWINIETY = "twf:naglowek-zwiniety";

/** Stopnie jak w ustawieniach Androida (od najmniejszego), 1 = domyślny. */
export const SKALE_EKRANU = [0.85, 1, 1.15, 1.3, 1.45] as const;
export const SKALE_CZCIONKI = [0.85, 1, 1.15, 1.3, 1.5, 1.75, 2] as const;

export interface Wyswietlanie {
  motyw: Motyw;
  ekran: number;
  czcionka: number;
  nieWygaszaj: boolean;
  blokada: BlokadaEkranu;
  /** Nagłówek pulpitu przyklejony u góry (false - przewija się z ekranem) */
  naglowekStaly: boolean;
  /** Nagłówek pulpitu zwinięty do jednego wiersza */
  naglowekZwiniety: boolean;
}

function czytaj(k: string): string | null {
  try { return localStorage.getItem(k); } catch { return null; }
}
function pisz(k: string, v: string): void {
  try { localStorage.setItem(k, v); } catch { /* tryb prywatny bez pamięci - działa do zamknięcia karty */ }
}
function stopien(v: string | null, lista: readonly number[]): number {
  const n = Number(v);
  return lista.includes(n) ? n : 1;
}

let stan: Wyswietlanie = {
  motyw: document.documentElement.getAttribute("data-iot-theme") === "light" ? "light" : "dark",
  ekran: stopien(czytaj(KLUCZ_EKRAN), SKALE_EKRANU),
  czcionka: stopien(czytaj(KLUCZ_CZCIONKA), SKALE_CZCIONKI),
  nieWygaszaj: czytaj(KLUCZ_NIE_WYGASZAJ) === "1",
  blokada: "czeka",
  naglowekStaly: czytaj(KLUCZ_NAGLOWEK_STALY) === "1",
  naglowekZwiniety: czytaj(KLUCZ_NAGLOWEK_ZWINIETY) === "1",
};
const sluchacze = new Set<() => void>();

function ustaw(s: Wyswietlanie): void {
  stan = s;
  zastosuj();
  for (const f of sluchacze) f();
}

/** Stan wyświetlania (okienko „Wyświetlanie” w belce). */
export function useWyswietlanie(): Wyswietlanie {
  return useSyncExternalStore((f) => { sluchacze.add(f); return () => sluchacze.delete(f); }, () => stan);
}

/** Ekran dotykowy (telefon, tablet) - tylko tam APP zmienia rozmiar wyświetlania. */
export const dotyk = () => matchMedia("(pointer: coarse)").matches;

/** Szerokość układu na telefonie przy skali: krótszy albo dłuższy bok ekranu (orientacja) podzielony przez skalę. */
function viewport(skala: number): string {
  if (skala === 1) return "width=device-width, initial-scale=1, viewport-fit=cover";
  const poziomo = matchMedia("(orientation: landscape)").matches;
  const bok = poziomo ? Math.max(screen.width, screen.height) : Math.min(screen.width, screen.height);
  return `width=${Math.round(bok / skala)}, initial-scale=${skala}, viewport-fit=cover`;
}

function zastosuj(): void {
  const html = document.documentElement;
  html.style.setProperty("--iot-czcionka", String(stan.czcionka));
  if (stan.naglowekStaly) html.setAttribute("data-iot-naglowek", "staly");
  else html.removeAttribute("data-iot-naglowek");
  document.querySelector('meta[name="viewport"]')?.setAttribute("content", viewport(dotyk() ? stan.ekran : 1));
}

zastosuj();
addEventListener("orientationchange", () => setTimeout(zastosuj, 200));

/** Następny (krok +1) albo poprzedni (−1) stopień skali; null - koniec zakresu. */
export function krok(lista: readonly number[], teraz: number, o: 1 | -1): number | null {
  const i = lista.indexOf(teraz) + o;
  return i >= 0 && i < lista.length ? lista[i]! : null;
}

export function ustawEkran(v: number): void {
  pisz(KLUCZ_EKRAN, String(v));
  ustaw({ ...stan, ekran: v });
}

export function ustawCzcionke(v: number): void {
  pisz(KLUCZ_CZCIONKA, String(v));
  ustaw({ ...stan, czcionka: v });
}

/** Nagłówek pulpitu przewija się z ekranem (v = true) albo stoi przyklejony u góry. */
export function ustawNaglowekPrzewija(v: boolean): void {
  pisz(KLUCZ_NAGLOWEK_STALY, v ? "0" : "1");
  ustaw({ ...stan, naglowekStaly: !v });
}

export function ustawNaglowekZwiniety(v: boolean): void {
  pisz(KLUCZ_NAGLOWEK_ZWINIETY, v ? "1" : "0");
  ustaw({ ...stan, naglowekZwiniety: v });
}

/** Kolor ze zmiennej motywu w miejscu elementu - dla płócien, które rysują poza CSS (wykres, ciecz w schemacie). */
export function kolorMotywu(el: Element, zmienna: string): string {
  return getComputedStyle(el).getPropertyValue(zmienna).trim() || "#888";
}

/** Przełączenie motywu jasny/ciemny - płótna czytają kolory od nowa i przerysowują się; zwraca odpięcie. */
export function naZmianeMotywu(f: () => void): () => void {
  const mo = new MutationObserver(f);
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ["data-iot-theme"] });
  return () => mo.disconnect();
}

/* ---------- Nie wygaszaj ekranu ---------- */
export const blokadaEkranuJest = () => natywna() || "wakeLock" in navigator;

let blokada: WakeLockSentinel | null = null;
let natywnaTrzyma = false;
let prosba = false;      // request('screen') w toku - focus i visibilitychange przychodzą razem
let ostatniaProba = 0;
let seria = 0;           // szybkie ponowienia po zwolnieniu przez system
let seriaOd = 0;
let straznik = 0;

const trzyma = () => natywnaTrzyma || (!!blokada && !blokada.released);
const stanBlokady = (b: BlokadaEkranu) => { if (stan.blokada !== b) ustaw({ ...stan, blokada: b }); };

async function zdobadzBlokade(): Promise<void> {
  if (prosba || trzyma() || !blokadaEkranuJest() || document.visibilityState !== "visible") return;
  // Dławik tylko po odmowie - po zwolnieniu przez system kolejna próba idzie od razu.
  if (stan.blokada === "odmowa" && Date.now() - ostatniaProba < 1000) return;
  ostatniaProba = Date.now();
  prosba = true;
  try {
    if (natywna()) {
      await KeepAwake.keepAwake();
      if (!stan.nieWygaszaj) { void KeepAwake.allowSleep().catch(() => undefined); return; }
      natywnaTrzyma = true;
      stanBlokady("trzyma");
      return;
    }
    const b = await navigator.wakeLock.request("screen");
    if (!stan.nieWygaszaj) { void b.release().catch(() => undefined); return; }   // wyłączone w trakcie prośby
    blokada = b;
    stanBlokady("trzyma");
    b.addEventListener("release", () => {
      if (blokada !== b) return;   // zwolniona przez nas (przełącznik wyłączony)
      blokada = null;
      stanBlokady("czeka");
      // Przy uporczywym zwalnianiu (oszczędzanie energii) po 3 próbach w 10 s dalej pilnuje tylko strażnik.
      const teraz = Date.now();
      if (teraz - seriaOd > 10_000) { seriaOd = teraz; seria = 0; }
      if (++seria <= 3) setTimeout(() => void odswiezBlokade(), 600);
    });
  } catch {
    // Odmowa: oszczędzanie energii, brak dotknięcia od startu, iOS < 16.4.
    stanBlokady("odmowa");
  } finally {
    prosba = false;
  }
}

function odswiezBlokade(): Promise<void> {
  if (stan.nieWygaszaj) {
    // Strażnik co 20 s: zdarzenie `release` potrafi przyjść przy widocznej stronie i nic innego blokady nie przywróci.
    if (!straznik) straznik = window.setInterval(() => void zdobadzBlokade(), 20_000);
    return zdobadzBlokade();
  }
  clearInterval(straznik);
  straznik = 0;
  if (natywnaTrzyma) {
    natywnaTrzyma = false;
    stanBlokady("czeka");
    return KeepAwake.allowSleep().catch(() => undefined);
  }
  const b = blokada;
  blokada = null;
  stanBlokady("czeka");
  return b ? b.release().catch(() => undefined) : Promise.resolve();
}

export function ustawNieWygaszaj(v: boolean): void {
  pisz(KLUCZ_NIE_WYGASZAJ, v ? "1" : "0");
  ustaw({ ...stan, nieWygaszaj: v });
  void odswiezBlokade();
}

document.addEventListener("visibilitychange", () => void odswiezBlokade());
// Powrót do aplikacji bywa zgłaszany tylko jednym z tych zdarzeń (bfcache, przełącznik aplikacji).
addEventListener("pageshow", () => void odswiezBlokade());
addEventListener("focus", () => void odswiezBlokade());
// Dotknięcie = świeży gest; przeglądarki, które odmówiły przy starcie, przyznają blokadę właśnie wtedy.
document.addEventListener("pointerdown", () => { if (stan.nieWygaszaj && !trzyma()) void zdobadzBlokade(); }, { passive: true, capture: true });
void odswiezBlokade();

export function ustawMotyw(m: Motyw): void {
  document.documentElement.setAttribute("data-iot-theme", m);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", m === "light" ? "#F5F3F0" : "#0B0E11");
  pisz(KLUCZ, m);
  ustaw({ ...stan, motyw: m });
}
